// The engine UI and the pointer (deck/ui/kit.js elements that called setInteractive()): the UI gets a press before
// the map does. deck/input.js asks uiPointerDown() first; a press on a UI element is the UI's until it is released
// (the map never sees it: no walk, no pan, no pick). The wheel over the UI goes to the element's onWheel (true:
// used, else the map zooms as usual). Points are CSS px. The hooks take Phaser's own pointer (game pixels).
// Also the registry tests and tours use: window.__deck.ui (list, get, click, type, scrollBy, at).

/** A Phaser pointer's position in CSS px (Phaser's own are in game pixels: CSS px x the render scale, scene.dpr
 *  from deck/scene.js; before that existed, Phaser's displayScale says the same). */
export function cssPoint(scene, p) {
  const k = scene.dpr, d = scene.scale.displayScale;
  return k ? { x: p.x / k, y: p.y / k } : { x: p.x / (d?.x || 1), y: p.y / (d?.y || 1) };
}
/** Draw order as a path from the layer down: [layer depth, index in parent, ...]; compares later-on-top. */
function zPath(el) {
  const path = [];
  let o = el.c;
  for (; o.parentContainer; o = o.parentContainer) path.push(o.parentContainer.getIndex(o));
  path.push(o.depth);
  return path.reverse();
}
function above(a, b) {
  for (let i = 0; i < Math.min(a.length, b.length); i++) if (a[i] !== b[i]) return a[i] > b[i];
  return a.length > b.length; // a child is over its parent
}
const inside = (r, x, y) => x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;

export function createHit(ui) {
  const items = new Set();
  const presses = new Map(); // pointer id -> {el, x0, y0, touch}
  let hovered = null, mouse = null, cursor = null;
  const scene = ui.scene, canvas = scene.game.canvas;
  /** The top-most shown, enabled, interactive element whose hit rect holds (x, y), or null. A disabled one takes no
   *  presses: they go to what's under it (its panel, or the map). */
  function at(x, y, touch = ui.touch) {
    let best = null, bestZ = null;
    for (const el of items) {
      if (!el.enabled || !el.shown()) continue;
      if (!inside(el.hitRect(touch), x, y)) continue;
      const z = zPath(el);
      if (!best || above(z, bestZ)) { best = el; bestZ = z; }
    }
    return best;
  }
  const pt = (p, extra) => { const { x, y } = cssPoint(scene, p); return { x, y, id: p.id, touch: !!p.wasTouch, event: p.event, ...extra }; };
  function setHover(el) {
    if (el === hovered) return;
    const was = hovered; hovered = el;
    try { was?.onOut?.(); } catch (e) { console.error(e); }
    try { el?.onOver?.(); } catch (e) { console.error(e); }
  }
  function down(p) {
    const old = presses.get(p.id); // a press whose release never came (a lost pointerup): over now
    if (old) { presses.delete(p.id); try { old.el.onUp?.({ ...pt(p), dx: 0, dy: 0 }, false); } catch (err) { console.error(err); } }
    const q = pt(p), el = at(q.x, q.y, q.touch), e = p.event;
    if (!el) return false;
    if (e && !q.touch && e.button > 0) return true; // a right or middle click on the UI: not the map's either
    presses.set(p.id, { el, x0: q.x, y0: q.y, touch: q.touch });
    if (!q.touch) setHover(el);
    try { el.onDown?.({ ...q, dx: 0, dy: 0 }); } catch (err) { console.error(err); }
    return true;
  }
  function move(p) {
    const q = pt(p), pr = presses.get(p.id);
    if (pr) { if (pr.el.enabled && !pr.el.destroyed) try { pr.el.onMove?.({ ...q, dx: q.x - pr.x0, dy: q.y - pr.y0 }); } catch (e) { console.error(e); } return; }
    if (q.touch || p.isDown) return;
    mouse = { x: q.x, y: q.y };
    setHover(at(q.x, q.y, false));
  }
  function up(p) {
    const pr = presses.get(p.id); if (!pr) return;
    presses.delete(p.id);
    const q = pt(p), el = pr.el;
    if (el.destroyed) return;
    const hit = inside(el.hitRect(pr.touch), q.x, q.y) && !p.event?.type?.endsWith('cancel');
    try { el.onUp?.({ ...q, dx: q.x - pr.x0, dy: q.y - pr.y0 }, hit); if (hit && el.enabled && el.shown()) el.onClick?.(q); } catch (e) { console.error(e); }
    if (pr.touch) setHover(null);
  }
  scene.input.on('pointermove', move);
  scene.input.on('pointerup', up); scene.input.on('pointerupoutside', up);
  scene.input.on('gameout', () => { mouse = null; setHover(null); });
  return {
    at, down,
    add(el) { items.add(el); },
    remove(el) { items.delete(el); if (hovered === el) hovered = null; for (const [id, pr] of presses) if (pr.el === el) presses.delete(id); },
    /** The wheel at a pointer: the element under it, then its parents, get onWheel(dy, pt) until one returns true
     *  (used: true, the map doesn't zoom); none did: false, the map zooms. A panel that should never let the map
     *  zoom under it sets onWheel = () => true. */
    wheel(p) {
      const q = pt(p), el = at(q.x, q.y, false);
      if (!el) return false;
      const dy = p.deltaY ?? p.event?.deltaY ?? 0;
      for (let e = el; e; e = e.parent) if (e.onWheel && e.enabled) { try { if (e.onWheel(dy, q)) return true; } catch (err) { console.error(err); return true; } }
      return false;
    },
    /** Is the press with this pointer id the UI's? */
    pressed(id) { return presses.has(id); },
    /** Per frame: the hovered element may have moved, hidden or gone; keep the cursor right. */
    frame() {
      if (mouse && !presses.size) setHover(at(mouse.x, mouse.y, false));
      if (hovered && (hovered.destroyed || !hovered.shown())) setHover(null);
      const want = hovered ? hovered.cursor ?? null : null;
      // only undo a cursor this set: the map's own (deck/input.js: pointer over a thing) is left as it is
      if (want !== cursor) { if (want) canvas.style.cursor = want; else if (cursor && canvas.style.cursor === cursor) canvas.style.cursor = ''; cursor = want; }
    },
    get hovered() { return hovered; },
  };
}

/** window.__deck.ui: the kit's elements by id, for tests, tours and the console. */
export function createRegistry(ui) {
  const els = new Map();
  const live = (id) => { const e = els.get(id); return e && !e.destroyed ? e : null; };
  const api = {
    /** Every registered element: [{id, kind, text, visible, enabled, bounds: {x, y, w, h} (CSS px)}] */
    list: () => [...els.values()].map((e) => e.info()),
    get: (id) => live(id)?.info() ?? null,
    /** Press element id as a click would: false if it's missing, hidden or disabled. */
    click(id) {
      const e = live(id); if (!e || !e.enabled || !e.shown()) return false;
      if (e.click) return e.click() !== false;
      const b = e.bounds(); e.onClick?.({ x: b.x + b.w / 2, y: b.y + b.h / 2, synthetic: true }); return true;
    },
    /** Type into a text field (deck/ui/textfield.js) */
    type(id, text) { const e = live(id); if (!e?.type) return false; e.type(text); return true; },
    /** Scroll a list (deck/ui/scroll.js) */
    scrollBy(id, dy) { const e = live(id); if (!e?.scrollBy) return false; e.scrollBy(dy); return true; },
    /** The id of the element that takes the pointer at (x, y) CSS px (null: the map, or an element without an id). */
    at(x, y) { const e = ui.hit.at(x, y); return e?.id ?? null; },
    /** The element itself (debugging). */
    el: (id) => live(id),
  };
  return {
    api,
    add(el) { if (els.has(el.id) && els.get(el.id) !== el && !els.get(el.id).destroyed) console.warn('ui: id used twice', el.id); els.set(el.id, el); },
    remove(el) { if (els.get(el.id) === el) els.delete(el.id); },
  };
}

// ---- deck/input.js's hooks. No UI (not started, or it failed: deck/ui/kit.js uiFrame), or a bug in it: nothing is
// claimed and the map works as before; these never throw into the map's listeners.
const hooked = (scene, f) => { const h = !scene.__uiFailed && scene.__ui?.hit; if (!h) return false; try { return !!f(h); } catch (e) { console.error('ui hook', e); return false; } };
/** A press (Phaser's pointer): true if the UI took it (the map must ignore this pointer until it is released). */
export const uiPointerDown = (scene, p) => hooked(scene, (h) => h.down(p));
/** The wheel (Phaser's pointer): true if the UI used it (don't zoom the map). */
export const uiWheel = (scene, p) => hooked(scene, (h) => h.wheel(p));
/** Is the UI at screen point (x, y) CSS px (no map hover or tooltip there)? */
export const uiAt = (scene, x, y) => hooked(scene, (h) => h.at(x, y, false));
